/* Editable payroll inputs (OT hours, bonus, absence days, other deductions) per employee.
 * Client-side validation mirrors domain validateAdjustment(); server 422 field errors are shown inline. */
import "../pay.css";
import { useMemo, useState, type KeyboardEvent } from "react";
import { useSetAdjustment } from "../../../data/hooks";
import { hourlyRate, validateAdjustment } from "../../../domain/payroll";
import type { Adjustment, Employee, PayrollRunView } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { fmtN } from "../../../lib/format";
import { EmptyState, IconButton, Money, Person, SearchBox, errMsg, useToast } from "../../../ui";
import { numOf, unknownEmp } from "./common";

type K = keyof Adjustment;
const KEYS: K[] = ["ot", "bonus", "absDays", "ded"];
type Draft = Record<K, string>;

export function validateDraft(d: Draft): Record<string, string> {
  const parsed: Partial<Adjustment> = {};
  const err: Record<string, string> = {};
  for (const k of KEYS) {
    const n = numOf(d[k]);
    if (Number.isNaN(n)) err[k] = "required";
    else parsed[k] = n;
  }
  return { ...validateAdjustment(parsed), ...err };
}

export default function AdjustmentsTable({ view, emp, locked, canEdit }: { view: PayrollRunView; emp: Map<number, Employee>; locked: boolean; canEdit: boolean }) {
  const { t, L, name } = useI18n();
  const toast = useToast();
  const save = useSetAdjustment();
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [srvErr, setSrvErr] = useState<Record<number, Record<string, string>>>({});
  const [saving, setSaving] = useState<number | null>(null);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return view.slips.filter((p) => {
      if (!s) return true;
      const e = emp.get(p.employeeId);
      return !!e && (e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s));
    });
  }, [q, view.slips, emp]);

  const base = (id: number): Draft => {
    const p = view.slips.find((x) => x.employeeId === id)!;
    return { ot: String(p.otHours), bonus: String(p.bonus), absDays: String(p.absDays), ded: String(p.ded) };
  };
  const setField = (id: number, k: K, v: string) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? base(id)), [k]: v } }));
    setSrvErr((e) => { if (!e[id]) return e; const n = { ...e }; delete n[id]; return n; });
  };

  const submit = async (id: number) => {
    const d = drafts[id];
    if (!d) return;
    const fe = validateDraft(d);
    if (Object.keys(fe).length) return;
    setSaving(id);
    try {
      await save.mutateAsync({ employeeId: id, adj: { ot: numOf(d.ot), bonus: numOf(d.bonus), absDays: numOf(d.absDays), ded: numOf(d.ded) } });
      setDrafts((x) => { const n = { ...x }; delete n[id]; return n; });
      toast(L("Adjustment saved · ", "تم حفظ التعديل · ") + name(emp.get(id) ?? unknownEmp(id)));
    } catch (e) {
      const f = (e as { fields?: Record<string, string> }).fields;
      if (f && Object.keys(f).length) setSrvErr((x) => ({ ...x, [id]: f }));
      else toast(errMsg(e), "bad");
    } finally {
      setSaving(null);
    }
  };
  const reset = (id: number) => {
    setDrafts((x) => { const n = { ...x }; delete n[id]; return n; });
    setSrvErr((x) => { const n = { ...x }; delete n[id]; return n; });
  };

  const LBL: Record<K, string> = { ot: L("OT hours", "ساعات إضافية"), bonus: t("Bonus"), absDays: L("Absent days", "أيام الغياب"), ded: t("Deductions") };
  const W: Record<K, number> = { ot: 76, bonus: 96, absDays: 66, ded: 90 };
  const dirtyCount = Object.keys(drafts).length;
  const disabled = locked || !canEdit;

  return (
    <section className="card mt pay-adj" data-testid="adjustments">
      <div className="toolbar">
        <SearchBox value={q} onChange={setQ} placeholder={L("Search employee or code", "ابحث بالاسم أو الرقم")} testId="adj-search" />
        <span className="spacer" />
        {dirtyCount > 0 && <span className="chip warn" data-testid="adj-dirty">{L(dirtyCount + " unsaved", dirtyCount + " غير محفوظ")}</span>}
      </div>
      {rows.length === 0 ? <EmptyState title={L("No matching employees", "لا يوجد موظفون مطابقون")} /> : (
        <div className="tw">
          <table className="tbl compact" data-testid="adj-table">
            <thead><tr><th>{t("Employee")}</th>{KEYS.map((k) => <th key={k} className="end">{LBL[k]}</th>)}<th className="end">{t("Loan")}</th><th className="end">{t("Net pay")}</th><th className="end"><span className="pay-sr">{t("Actions")}</span></th></tr></thead>
            <tbody>
              {rows.map((p) => {
                const e = emp.get(p.employeeId);
                const d = drafts[p.employeeId];
                const vals = d ?? base(p.employeeId);
                const fe = { ...(d ? validateDraft(d) : {}), ...(srvErr[p.employeeId] ?? {}) };
                const over = view.art92.includes(p.employeeId);
                return (
                  <tr key={p.employeeId} data-testid={"adj-row-" + p.employeeId} className={over ? "pay-over" : undefined}>
                    <td>{e ? <Person e={e} meta={<><Money value={hourlyRate(e)} plain />{L(" /h", " /س")}</>} /> : "#" + p.employeeId}</td>
                    {KEYS.map((k) => {
                      const nm = e ? name(e) : String(p.employeeId);
                      return (
                        <td key={k} className="end">
                          <input className={"inp num" + (fe[k] ? " bad" : "")} style={{ width: W[k], minHeight: 34, textAlign: "end", padding: "0 8px" }}
                            type="number" min={0} step={k === "ot" ? 0.5 : 1} inputMode="decimal" value={vals[k]} disabled={disabled}
                            aria-label={LBL[k] + " · " + nm} aria-invalid={fe[k] ? true : undefined} title={fe[k] || undefined}
                            data-testid={"adj-" + k + "-" + p.employeeId}
                            onChange={(ev) => setField(p.employeeId, k, ev.target.value)}
                            onKeyDown={(ev: KeyboardEvent<HTMLInputElement>) => { if (ev.key === "Enter") { ev.preventDefault(); void submit(p.employeeId); } if (ev.key === "Escape" && d) { ev.stopPropagation(); reset(p.employeeId); } }} />
                          {fe[k] && <div className="err xs" role="alert" data-testid={"adj-err-" + k + "-" + p.employeeId}>{fe[k]}</div>}
                        </td>
                      );
                    })}
                    <td className="end">{p.loan ? <Money value={-p.loan} dec={0} plain /> : <span className="muted">—</span>}</td>
                    <td className="end"><b><Money value={p.net} plain /></b>{over && <div className="xs" style={{ color: "var(--danger)" }}>{L("Over 50% cap", "يتجاوز حد 50%")}</div>}</td>
                    <td className="end">
                      {d && (
                        <div className="row nw" style={{ justifyContent: "flex-end" }}>
                          <IconButton icon="x" label={L("Discard changes", "تجاهل التغييرات")} onClick={() => reset(p.employeeId)} data-testid={"adj-reset-" + p.employeeId} />
                          <IconButton icon={saving === p.employeeId ? "refresh" : "check"} label={L("Save adjustment", "حفظ التعديل")} onClick={() => void submit(p.employeeId)} disabled={Object.keys(validateDraft(d)).length > 0 || saving === p.employeeId} data-testid={"adj-save-" + p.employeeId} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr><td>{t("Total")} · {view.totals.headcount}</td><td className="end num">{fmtN(view.slips.reduce((a, s) => a + s.otHours, 0), 1)}</td><td className="end"><Money value={view.totals.bonus} dec={0} plain /></td><td /><td /><td /><td className="end"><Money value={view.totals.net} /></td><td /></tr></tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
